import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Button, FormGroup, Input, Label, Select, TextArea } from '../../../components/shared/StyledComponents'
import { api, LinkedInProjectDetail, LinkedInProjectInput } from '../../../services/api'
import { Alert, Chip } from './linkedinStyles'
import { ItemPicker, itemLabel, PickedItem, PickedRedmine, RedmineProjectPicker } from './projectPickers'

/**
 * Create / edit a SOTA project, and create the project of an intern.
 *
 * On edit every list is sent in full: the API replaces the lists it receives, so
 * what the form shows is exactly what is stored afterwards.
 */

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 4vh 1rem;
    overflow-y: auto;
    z-index: 1000;
`

const Dialog = styled.div`
    background: ${({ theme }) => theme.colors.cardBg};
    border-radius: ${({ theme }) => theme.borderRadius.lg};
    width: min(680px, 100%);
    box-shadow: ${({ theme }) => theme.shadows.xl};
`

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`

const Close = styled.button`
    border: none;
    background: none;
    font-size: 1.5rem;
    cursor: pointer;
    line-height: 1;
`

const Body = styled.div`
    padding: 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
`

const Footer = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    padding: 1rem 1.25rem;
    border-top: 1px solid ${({ theme }) => theme.colors.border};
`

const Techs = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    margin-bottom: 0.4rem;
`

const RemoveTech = styled.button`
    border: none;
    background: none;
    cursor: pointer;
    font: inherit;
    padding: 0 0 0 0.2rem;
`

export const apiError = (err: any, fallback: string): string => {
    const detail = err?.response?.data?.detail
    return typeof detail === 'string' ? detail : fallback
}

const Shell: React.FC<{ title: string; onClose: () => void; footer: React.ReactNode; children: React.ReactNode }> = ({
    title,
    onClose,
    footer,
    children,
}) => (
    <Overlay onMouseDown={onClose}>
        <Dialog role="dialog" aria-label={title} onMouseDown={(e) => e.stopPropagation()}>
            <Header>
                <strong>{title}</strong>
                <Close type="button" aria-label="Cerrar" onClick={onClose}>
                    &times;
                </Close>
            </Header>
            <Body>{children}</Body>
            <Footer>{footer}</Footer>
        </Dialog>
    </Overlay>
)

interface ProjectFormProps {
    /** The project being edited; absent to create. */
    project?: LinkedInProjectDetail | null
    onClose: () => void
    onSaved: (project: LinkedInProjectDetail) => void
}

export const ProjectFormModal: React.FC<ProjectFormProps> = ({ project, onClose, onSaved }) => {
    const editing = !!project
    const [name, setName] = useState(project?.name ?? '')
    const [description, setDescription] = useState(project?.description ?? '')
    const [technologies, setTechnologies] = useState<string[]>(project?.technologies ?? [])
    const [techInput, setTechInput] = useState('')
    const [repos, setRepos] = useState<PickedItem[]>(
        (project?.repos ?? []).map((r) => ({ id: r.id, label: itemLabel('repo', r) }))
    )
    const [papers, setPapers] = useState<PickedItem[]>(
        (project?.papers ?? []).map((p) => ({ id: p.id, label: itemLabel('paper', p) }))
    )
    const [posts, setPosts] = useState<PickedItem[]>(
        (project?.posts ?? []).map((p) => ({ id: p.id, label: itemLabel('post', p) }))
    )
    const [redmine, setRedmine] = useState<PickedRedmine | null>(
        project?.redmine_project_id
            ? { id: project.redmine_project_id, name: project.redmine_project_name || `#${project.redmine_project_id}` }
            : null
    )
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const addTech = () => {
        const parts = techInput
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean)
        if (parts.length === 0) return
        // Case-insensitive, also among the ones typed together; the first spelling wins.
        const known = new Set(technologies.map((t) => t.toLowerCase()))
        const added: string[] = []
        for (const t of parts) {
            if (known.has(t.toLowerCase())) continue
            known.add(t.toLowerCase())
            added.push(t)
        }
        setTechnologies([...technologies, ...added])
        setTechInput('')
    }

    const save = async () => {
        if (name.trim().length < 2) {
            setError('El nombre necesita al menos 2 caracteres.')
            return
        }
        setSaving(true)
        setError(null)
        const body: LinkedInProjectInput = {
            name: name.trim(),
            description: description.trim() || null,
            technologies,
            repo_ids: repos.map((r) => r.id),
            paper_ids: papers.map((p) => p.id),
            post_ids: posts.map((p) => p.id),
        }
        if (redmine) body.redmine_project_id = redmine.id
        else if (editing && project?.redmine_project_id) body.redmine_project_id = 0
        try {
            const res = editing
                ? await api.linkedin.updateProject(project!.id, body)
                : await api.linkedin.createProject({ ...body, name: body.name! })
            onSaved(res.data)
        } catch (err) {
            setError(apiError(err, 'No se pudo guardar el proyecto.'))
        } finally {
            setSaving(false)
        }
    }

    return (
        <Shell
            title={editing ? '✏️ Editar proyecto' : '🧩 Nuevo proyecto SOTA'}
            onClose={onClose}
            footer={
                <>
                    <Button type="button" variant="secondary" onClick={onClose}>
                        Cancelar
                    </Button>
                    <Button type="button" variant="primary" disabled={saving} onClick={save}>
                        {saving ? 'Guardando…' : 'Guardar'}
                    </Button>
                </>
            }
        >
            {error && <Alert $type="error">{error}</Alert>}
            <FormGroup>
                <Label htmlFor="project-name">Nombre</Label>
                <Input id="project-name" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
            </FormGroup>
            <FormGroup>
                <Label htmlFor="project-description">Descripción</Label>
                <TextArea
                    id="project-description"
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                />
            </FormGroup>
            <FormGroup>
                <Label htmlFor="project-tech">Tecnologías</Label>
                <Techs>
                    {technologies.map((t) => (
                        <Chip key={t} $bg="#e8f5e9" $color="#1b5e20">
                            {t}
                            <RemoveTech
                                type="button"
                                aria-label={`Quitar ${t}`}
                                onClick={() => setTechnologies(technologies.filter((x) => x !== t))}
                            >
                                ×
                            </RemoveTech>
                        </Chip>
                    ))}
                </Techs>
                <Input
                    id="project-tech"
                    value={techInput}
                    placeholder="Escribe y pulsa Intro (varias separadas por coma)"
                    onChange={(e) => setTechInput(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault()
                            addTech()
                        }
                    }}
                    onBlur={addTech}
                />
            </FormGroup>
            <RedmineProjectPicker value={redmine} onChange={setRedmine} />
            <ItemPicker kind="repo" selected={repos} onChange={setRepos} />
            <ItemPicker kind="paper" selected={papers} onChange={setPapers} />
            <ItemPicker kind="post" selected={posts} onChange={setPosts} />
        </Shell>
    )
}

interface PracticasProps {
    onClose: () => void
    onSaved: (project: LinkedInProjectDetail) => void
}

interface Intern {
    id: number
    display_name?: string
    username: string
}

/** The project of one intern: fixed name, linked to their Redmine training project. */
export const PracticasProjectModal: React.FC<PracticasProps> = ({ onClose, onSaved }) => {
    const [interns, setInterns] = useState<Intern[]>([])
    const [userId, setUserId] = useState<number | ''>('')
    const [redmine, setRedmine] = useState<PickedRedmine | null>(null)
    const [manual, setManual] = useState(false)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        api.users
            .getAll()
            .then((res) => {
                const rows: any[] = Array.isArray(res.data) ? res.data : res.data?.users || []
                setInterns(rows.filter((u) => u.user_type === 'intern' && (u.username || '').toLowerCase() !== 'admin'))
            })
            .catch(() => setError('No se pudo cargar la lista de personas en prácticas.'))
    }, [])

    const create = async () => {
        if (userId === '') return
        setSaving(true)
        setError(null)
        try {
            const res = await api.linkedin.createPracticasProject({
                user_id: userId,
                ...(redmine ? { redmine_project_id: redmine.id } : {}),
            })
            onSaved(res.data)
        } catch (err) {
            // Several candidates, or none: let the admin pick the training project by hand.
            const status = (err as any)?.response?.status
            if (status === 409 || status === 422) setManual(true)
            setError(apiError(err, 'No se pudo crear el proyecto de prácticas.'))
        } finally {
            setSaving(false)
        }
    }

    return (
        <Shell
            title="🎓 Proyecto de prácticas"
            onClose={onClose}
            footer={
                <>
                    <Button type="button" variant="secondary" onClick={onClose}>
                        Cancelar
                    </Button>
                    <Button type="button" variant="primary" disabled={saving || userId === ''} onClick={create}>
                        {saving ? 'Creando…' : 'Crear proyecto'}
                    </Button>
                </>
            }
        >
            <p style={{ margin: 0, fontSize: '0.9rem' }}>
                Se llama <code>031-228_PLEXUS_PracticasIA-nombre-apellido1</code> y se vincula a su proyecto «Plan de
                formación» de Redmine. Luego puedes editarlo para añadir repos, papers, publicaciones y tecnologías.
            </p>
            {error && <Alert $type="error">{error}</Alert>}
            <FormGroup>
                <Label htmlFor="practicas-user">Persona</Label>
                <Select
                    id="practicas-user"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value ? Number(e.target.value) : '')}
                >
                    <option value="">Selecciona a quien está en prácticas…</option>
                    {interns.map((u) => (
                        <option key={u.id} value={u.id}>
                            {u.display_name || u.username}
                        </option>
                    ))}
                </Select>
            </FormGroup>
            {manual ? (
                <RedmineProjectPicker value={redmine} onChange={setRedmine} />
            ) : (
                <button
                    type="button"
                    style={{ alignSelf: 'flex-start', border: 'none', background: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => setManual(true)}
                >
                    Elegir el proyecto de formación a mano
                </button>
            )}
        </Shell>
    )
}
